import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useAuth } from "../auth/AuthContext";
import {
  MARK_REASON_OPTIONS,
  MIN_MARK_COMMENT_CHARS,
  isMarkJustificationValid,
  latestInboundEvent,
  refreshTimelineBeforeMark,
  requiresMarkComment,
  requiresJustification,
} from "./markDisposition";
import {
  useQuote360,
  useMarkQuoteConversation,
  useQuoteActionPreview,
  useSendQuoteAction,
  useCreateQuoteReminder,
  useCreateManualQuoteReminder,
  useCancelManualQuoteReminder,
  useSetClientGhost,
  useUndoQuoteConversation,
  type ConversationDisposition,
  type Quote360,
  type QuoteActionPreview,
  type QuoteActionChannel,
  type QuoteActionType,
} from "../queries/useQuote360";
import { GhostMark, UNGHOST_REMINDER } from "./GhostMark";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Timeline } from "./Timeline";

function formatUsd(value?: number) {
  return `$${Math.round(value || 0).toLocaleString()}`;
}

function formatDate(value?: string) {
  if (!value) return ", ";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

const DEFAULT_TENANT_TIME_ZONE = "America/New_York";

function timeZoneLabel(timeZone: string) {
  try {
    const part = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longGeneric",
    }).formatToParts(new Date()).find((item) => item.type === "timeZoneName");
    return part?.value || timeZone;
  } catch {
    return timeZone;
  }
}

function tenantDateTimeParts(value?: string | null, timeZone = DEFAULT_TENANT_TIME_ZONE) {
  const date = value ? new Date(value) : new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  const hour = parts.hour === "24" ? "00" : parts.hour;
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${hour}:${parts.minute}`,
  };
}

export function Quote360View({ quoteId }: { quoteId: string }) {
  const { user } = useAuth();
  const { data, isLoading, isError } = useQuote360(quoteId);
  const isSales = user?.role === "sales";
  const canManageReminders = user?.role === "owner" || user?.role === "gm";

  if (isLoading) return <Card className="p-6 text-sm text-slate-500">Loading Quote 360...</Card>;
  if (isError || !data) {
    return (
      <Card className="p-6">
        <div className="text-sm font-medium text-slate-950">Quote 360 unavailable</div>
        <p className="mt-1 text-sm text-slate-500">Check the backend endpoint, admin token, or quote id.</p>
      </Card>
    );
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <Badge variant="slate">Quote 360</Badge>
          <h1 className="mt-3 text-2xl font-semibold text-slate-950">Quote #{data.quote.quote_id}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {data.customer.full_name}
            <GhostMark ghosted={data.ghost?.ghosted} />
            {" "}· {formatUsd(data.quote.total_usd)} · {data.quote.status}
          </p>
        </div>
        <QuoteAction data={data} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Customer Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <SummaryItem label="Customer" value={data.customer.full_name} />
            <SummaryItem label="Email" value={data.customer.email} />
            <SummaryItem label="Phone" value={data.customer.phone || ", "} />
            <SummaryItem label="Created" value={formatDate(data.quote.created_on)} />
          </div>
          <GhostPanel data={data} canToggle={!isSales} />
          <div className="mt-4 flex flex-wrap gap-3">
            {!isSales ? (
              <Link
                className="inline-flex text-sm font-medium text-blue-700 hover:text-blue-900"
                to="/customers/$contactId"
                params={{ contactId: String(data.customer.id) }}
              >
                Open Customer 360
              </Link>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <ReminderCard data={data} canManageReminders={canManageReminders} />

      <Card>
        <CardHeader>
          <CardTitle>Quote Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <Timeline events={data.timeline} />
        </CardContent>
      </Card>
    </section>
  );
}

function ReminderCard({ data, canManageReminders }: { data: Quote360; canManageReminders: boolean }) {
  const reminder = data.followup_reminder;
  const suggestion = data.followup_reminder_suggestion;
  const createReminder = useCreateQuoteReminder(data.quote.quote_id);
  const createManualReminder = useCreateManualQuoteReminder(data.quote.quote_id);
  const cancelManualReminder = useCancelManualQuoteReminder(data.quote.quote_id);
  const activeManual = reminder?.source === "quote360_manual" && reminder.status === "pending" ? reminder : null;
  const tenantTimeZone = data.tenant?.timezone || DEFAULT_TENANT_TIME_ZONE;
  const tenantTimeZoneLabel = data.tenant?.timezone_label || timeZoneLabel(tenantTimeZone);
  const item = reminder || suggestion;
  const [reminderDate, setReminderDate] = useState(item?.reminder_date || "");
  const initialManual = tenantDateTimeParts(activeManual?.due_at_utc, tenantTimeZone);
  const [manualDate, setManualDate] = useState(initialManual.date);
  const [manualTime, setManualTime] = useState(initialManual.time);
  const [manualComment, setManualComment] = useState(activeManual?.comment || activeManual?.reason || "");
  useEffect(() => {
    const next = tenantDateTimeParts(activeManual?.due_at_utc, tenantTimeZone);
    setManualDate(next.date);
    setManualTime(next.time);
    setManualComment(activeManual?.comment || activeManual?.reason || "");
  }, [activeManual?.due_at_utc, activeManual?.comment, activeManual?.reason, tenantTimeZone]);
  const isPending = item?.status === "pending";
  const infoText = item?.info_text || `Reminder scheduled for ${formatDate(item?.reminder_date)}: ${item?.reason}`;
  const manualDisabled = createManualReminder.isPending || !manualDate || !manualTime || !manualComment.trim();
  if (!item && !canManageReminders) return null;
  return (
    <Card className={isPending ? "border-blue-200 bg-blue-50" : item ? "border-amber-200 bg-amber-50" : ""}>
      <CardContent className="space-y-4 p-4">
        {item ? (
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                {isPending ? "Reminder scheduled" : "Suggested reminder"}
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-950">
                {isPending ? infoText : `Follow up on ${formatDate(item.reminder_date)}`}
              </div>
              {!isPending ? <p className="mt-1 text-sm text-slate-600">{item.reason}</p> : null}
              {item.draft_seed ? <p className="mt-1 text-xs text-slate-500">{item.draft_seed}</p> : null}
            </div>
            {!isPending ? <div className="flex flex-col gap-2 md:w-[220px]">
              <Input type="date" value={reminderDate} onChange={(event) => setReminderDate(event.target.value)} />
              <Button
                disabled={createReminder.isPending || !reminderDate}
                onClick={() => createReminder.mutate({
                  reminder_date: reminderDate,
                  reason: item.reason,
                  source_email_response_id: item.source_email_response_id || null,
                })}
              >
                Schedule reminder
              </Button>
            </div> : null}
          </div>
        ) : null}
        {canManageReminders ? <div className={item ? "border-t border-slate-200 pt-4" : ""}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Set reminder</div>
          <p className="mt-1 text-sm text-slate-600">
            Set a reminder that pops into Today at the date and time you pick, with your own note ({tenantTimeZoneLabel}).
          </p>
          <div className="mt-3 grid gap-3 md:grid-cols-[150px_140px_minmax(220px,1fr)_auto] md:items-start">
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Date</div>
              <Input type="date" value={manualDate} onChange={(event) => setManualDate(event.target.value)} />
            </div>
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Time ({tenantTimeZoneLabel})</div>
              <Input
                type="time"
                title={`Time (${tenantTimeZoneLabel})`}
                value={manualTime}
                onChange={(event) => setManualTime(event.target.value)}
              />
            </div>
            <textarea
              className="min-h-[42px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              value={manualComment}
              onChange={(event) => setManualComment(event.target.value)}
              placeholder="Add Antonio or Marc's note"
            />
            <div className="flex gap-2">
              <Button
                disabled={manualDisabled}
                onClick={() => createManualReminder.mutate({ date: manualDate, time: manualTime, comment: manualComment })}
              >
                {activeManual ? "Reschedule" : "Set reminder"}
              </Button>
              {activeManual?.id ? (
                <Button
                  variant="secondary"
                  disabled={cancelManualReminder.isPending}
                  onClick={() => cancelManualReminder.mutate({ reminderId: activeManual.id as number })}
                >
                  Cancel
                </Button>
              ) : null}
            </div>
          </div>
        </div> : null}
      </CardContent>
    </Card>
  );
}

function GhostPanel({ data, canToggle }: { data: Quote360; canToggle: boolean }) {
  const ghost = data.ghost;
  const ghosted = Boolean(ghost?.ghosted);
  const update = useSetClientGhost(data.quote.quote_id);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  if (!ghosted && !canToggle) return null;
  return (
    <div className={ghosted ? "mt-4 rounded-lg border border-slate-300 bg-slate-50 p-4" : "mt-4 rounded-lg border border-slate-200 p-4"}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Ghost mode</div>
      <p className="mt-1 text-sm text-slate-600">
        {ghosted
          ? "Outbound SMS, email, reminders, and review requests are stopped for this client. They are not told."
          : "Silently stop every outbound message to this client. They are not told, and the quote is not marked lost."}
      </p>
      {ghosted && ghost?.ghost_reason ? (
        <p className="mt-2 text-sm text-slate-800">
          Internal reason: {ghost.ghost_reason}
          {ghost.ghosted_by ? ` · ${ghost.ghosted_by}` : ""}
        </p>
      ) : null}
      {canToggle && !ghosted ? (
        <textarea
          className="mt-3 min-h-[64px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Internal reason. Never sent to the client."
        />
      ) : null}
      {error ? <p className="mt-2 text-sm text-red-700">{error}</p> : null}
      {ghosted ? <p className="mt-2 text-sm text-slate-600">{UNGHOST_REMINDER}</p> : null}
      {canToggle ? (
        <Button
          className="mt-3"
          variant={ghosted ? "secondary" : "default"}
          disabled={update.isPending || (!ghosted && reason.trim().length < 3)}
          onClick={() => {
            setError("");
            update.mutate(
              ghosted ? { ghosted: false } : { ghosted: true, reason: reason.trim() },
              { onError: (err) => setError(err instanceof Error ? err.message : "Ghost update failed") },
            );
          }}
        >
          {ghosted ? "Remove ghost mode" : "Ghost this client"}
        </Button>
      ) : null}
    </div>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-1 truncate text-sm font-medium text-slate-950">{value}</div>
    </div>
  );
}

function QuoteAction({ data }: { data: Quote360 }) {
  const assistant = data.action_assistant;
  const [modalOpen, setModalOpen] = useState(false);
  const [actionType, setActionType] = useState<QuoteActionType>(assistant?.recommended_action || "helpful_followup");
  const [channel, setChannel] = useState<QuoteActionChannel>("email");
  const [promoAmount, setPromoAmount] = useState("$75");
  const [draft, setDraft] = useState<QuoteActionPreview | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const preview = useQuoteActionPreview(data.quote.quote_id);
  const send = useSendQuoteAction(data.quote.quote_id);
  const markConversation = useMarkQuoteConversation(data.quote.quote_id);
  const undoConversation = useUndoQuoteConversation(data.quote.quote_id);
  const queryClient = useQueryClient();
  const [lastMark, setLastMark] = useState<{
    noteId: number;
    label: string;
    disposition: ConversationDisposition;
  } | null>(null);
  const [markDialog, setMarkDialog] = useState<{
    disposition: ConversationDisposition;
    label: string;
  } | null>(null);
  const [markReason, setMarkReason] = useState("");
  const [markComment, setMarkComment] = useState("");
  const [markRefreshError, setMarkRefreshError] = useState<string | null>(null);

  if (assistant) {
    const warning = Boolean(assistant.guard?.warning);
    const blocked = (assistant.status === "blocked" || assistant.guard?.blocked) && !warning;
    const canManualSms = Boolean(data.customer.phone);
    const canManualEmail = Boolean(data.customer.email);
    async function openPreview(nextType = actionType, nextChannel = channel) {
      setActionType(nextType);
      setChannel(nextChannel);
      const rendered = await preview.mutateAsync({ actionType: nextType, channel: nextChannel, promoAmount });
      setDraft(rendered);
      setSubject(rendered.subject);
      setBody(rendered.body);
      setModalOpen(true);
    }
    async function openManualReply(nextChannel: QuoteActionChannel) {
      await openPreview("manual_reply", nextChannel);
    }
    async function sendDraft(force = false) {
      const shouldForce = force || Boolean(draft?.guard?.warning);
      if (shouldForce && !force) {
        const confirmed = window.confirm("Murray found a prior touch or handled marker. Send anyway?");
        if (!confirmed) return;
      }
      await send.mutateAsync({ actionType, channel, subject, body, force: shouldForce });
      setModalOpen(false);
      setDraft(null);
    }
    async function markHandled(disposition: ConversationDisposition, label: string, note?: string) {
      if (requiresJustification(disposition)) {
        // Handled/Closed are deliberate acts now: the confirmation dialog
        // shows the customer's last message and requires a reason. Named
        // reasons are sufficient; only Other requires a comment.
        // Refetch FIRST and only open the dialog once the refetch settled
        // successfully: a fire-and-forget invalidate let a fast operator
        // confirm against a stale timeline while a newer inbound message was
        // still in flight (PR #755 review debt).
        setMarkReason("");
        setMarkComment("");
        setMarkRefreshError(null);
        const fresh = await refreshTimelineBeforeMark(queryClient, data.quote.quote_id);
        if (!fresh) {
          setMarkRefreshError("Could not refresh the conversation. Check the connection and try again before marking.");
          return;
        }
        setMarkDialog({ disposition, label });
        return;
      }
      const result = await markConversation.mutateAsync({ disposition, note });
      setLastMark({ noteId: result.note_id, label, disposition });
    }
    async function confirmJustifiedMark() {
      if (!markDialog || !isMarkJustificationValid(markReason, markComment)) return;
      const result = await markConversation.mutateAsync({
        disposition: markDialog.disposition,
        reason_code: markReason,
        comment: markComment.trim(),
      });
      setLastMark({ noteId: result.note_id, label: markDialog.label, disposition: markDialog.disposition });
      setMarkDialog(null);
    }
    async function undoLastMark() {
      if (!lastMark) return;
      await undoConversation.mutateAsync({ noteId: lastMark.noteId });
      setLastMark(null);
    }
    const smsBodyHasQualityCap = channel === "sms" && actionType !== "manual_reply";
    return (
      <>
        <div className={`rounded-lg border p-3 shadow-sm md:min-w-[320px] ${blocked || warning ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
          <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Recommended action
            <HelpTip text="Murray suggests the next safest action from the latest email, SMS, quote status, and recent outbound history. You still review before anything is sent." />
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-950">
            {blocked ? blockedActionLabel(assistant.guard?.code) : labelForAction(assistant.recommended_action)}
          </div>
          <p className="mt-1 text-xs text-slate-600">{assistant.reason}</p>
          {blocked ? (
            <div className="mt-3 rounded-md bg-white/70 p-2 text-xs text-amber-800">{assistant.guard.reason}</div>
          ) : (
            <>
              {warning ? (
                <div className="mt-3 rounded-md bg-white/70 p-2 text-xs text-amber-800">{assistant.guard.reason}</div>
              ) : null}
              <div className="mt-3 flex gap-2">
                <Button className="flex-1" onClick={() => openPreview(assistant.recommended_action || "helpful_followup", channel)} disabled={preview.isPending}>
                  {primaryButtonLabel(assistant.recommended_action)}
                </Button>
                <Button className="flex-1" variant="secondary" onClick={() => setModalOpen(true)} title="Choose email or SMS, then review the draft before sending.">
                  Choose
                </Button>
              </div>
            </>
          )}
          {canManualSms || canManualEmail ? (
            <div className="mt-3 border-t border-slate-200 pt-3">
              <div className="grid gap-2 sm:grid-cols-2">
                {canManualSms ? (
                  <Button className="w-full" variant="secondary" onClick={() => openManualReply("sms")} disabled={preview.isPending}>
                    Manual SMS
                  </Button>
                ) : null}
                {canManualEmail ? (
                  <Button className="w-full" variant="secondary" onClick={() => openManualReply("email")} disabled={preview.isPending}>
                    Manual email
                  </Button>
                ) : null}
              </div>
              <p className="mt-1 text-[11px] leading-snug text-slate-500">
                Human reply only. Useful for confirming scheduling, answering an operational question, or sending a personal follow-up outside the automation cadence.
              </p>
            </div>
          ) : null}
          <div className="mt-3 border-t border-slate-200 pt-2">
            <div className="mb-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Decision
              <HelpTip text="Email and SMS sends from Murray clear Today automatically. Use Handle only for work completed outside Murray, such as a phone call or a private-phone SMS." />
            </div>
            <div className="grid gap-2">
              <QuickMarkButton
                label="Handled"
                disabled={markConversation.isPending}
                description="Record a call, private-phone SMS, or another action completed outside Murray."
                onClick={() => markHandled("HANDLED", "Handled")}
              />
              <QuickMarkButton
                label="Keep follow-up"
                disabled={markConversation.isPending}
                description="Keep Murray follow-up active. Customer questions and requests still stay in Today."
                onClick={() => markHandled(
                  "KEEP_FOLLOWUP",
                  "Keep follow-up",
                  "Murray follow-up stays active. Customer questions and requests still need human review.",
                )}
              />
              <QuickMarkButton
                label="Closed"
                disabled={markConversation.isPending}
                description="Stop follow-up for this quote. Use only for clear no, no fit, old file, or fully closed case."
                danger
                onClick={() => markHandled("CLOSED", "Closed")}
              />
            </div>
            {lastMark ? (
              <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                <span>{lastMark.label} saved.</span>
                <button
                  type="button"
                  className="font-semibold text-emerald-900 underline decoration-emerald-300 underline-offset-2 disabled:opacity-50"
                  disabled={undoConversation.isPending}
                  onClick={() => undoLastMark()}
                >
                  Undo
                </button>
              </div>
            ) : null}
            {markRefreshError ? (
              <div className="mt-2 text-xs text-red-700">{markRefreshError}</div>
            ) : null}
            {markConversation.isError ? (
              <div className="mt-2 text-xs text-red-700">{String(markConversation.error.message)}</div>
            ) : null}
            {undoConversation.isError ? (
              <div className="mt-2 text-xs text-red-700">{String(undoConversation.error.message)}</div>
            ) : null}
          </div>
        </div>
        {markDialog ? (
          <MarkJustificationDialog
            data={data}
            label={markDialog.label}
            danger={markDialog.disposition === "CLOSED" || markDialog.disposition === "closed"}
            reason={markReason}
            comment={markComment}
            pending={markConversation.isPending}
            errorMessage={markConversation.isError ? String(markConversation.error.message) : null}
            onReasonChange={setMarkReason}
            onCommentChange={setMarkComment}
            onCancel={() => setMarkDialog(null)}
            onConfirm={() => confirmJustifiedMark()}
          />
        ) : null}
        {modalOpen ? (
          <div className="mobile-dialog-backdrop">
            <div className="mobile-dialog-panel max-w-3xl p-4 md:p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">Prepare quote action</h3>
                  <p className="mt-1 text-sm text-slate-500">Review before sending. Murray prepares the draft, you stay in control.</p>
                </div>
                <Button className="shrink-0" variant="ghost" onClick={() => setModalOpen(false)}>Close</Button>
              </div>
              {actionType === "manual_reply" ? (
                <div className="mt-4 rounded-md border border-blue-100 bg-blue-50 p-3 text-xs leading-relaxed text-blue-900">
                  Manual {channel === "sms" ? "SMS" : "email"} reply. This bypasses automated follow-up blocks, but opt-out and content safety checks still apply.
                </div>
              ) : (
                <div className="mt-4 inline-flex rounded-md border border-slate-200 bg-slate-50 p-1 text-sm">
                  <button
                    type="button"
                    className={`rounded px-3 py-1.5 font-medium ${channel === "email" ? "bg-white text-slate-950 shadow-sm" : "text-slate-500"}`}
                    onClick={() => openPreview(actionType, "email")}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    className={`rounded px-3 py-1.5 font-medium ${channel === "sms" ? "bg-white text-slate-950 shadow-sm" : "text-slate-500"}`}
                    onClick={() => openPreview(actionType, "sms")}
                  >
                    SMS
                  </button>
                </div>
              )}
              <div className="mt-4 grid gap-3 md:grid-cols-[240px_1fr]">
                <div className="space-y-2">
                  {actionType === "manual_reply" ? (
                    <div className="rounded-md border border-slate-100 bg-slate-50 p-3 text-sm text-slate-600">
                      Write the customer reply yourself. Murray will not auto-send, enforce cadence, or pick a campaign template here.
                    </div>
                  ) : (
                    (assistant.available_actions.length ? assistant.available_actions : [{ type: "helpful_followup" as const, label: "Send helpful follow-up" }]).map((action) => (
                      <button
                        key={action.type}
                        type="button"
                        className={`w-full rounded-md border p-3 text-left text-sm ${actionType === action.type ? "border-slate-900 bg-slate-50" : "border-slate-100 hover:bg-slate-50"}`}
                        onClick={() => openPreview(action.type, channel)}
                      >
                        <div className="font-medium text-slate-950">{action.label}</div>
                        <div className="mt-1 text-xs text-slate-500">{reasonForAction(action.type)}</div>
                      </button>
                    ))
                  )}
                  {actionType === "schedule_opening" ? (
                    <label className="block">
                      <span className="text-xs font-medium text-slate-600">Promotion amount</span>
                      <Input className="mt-1" value={promoAmount} onChange={(event) => setPromoAmount(event.target.value)} onBlur={() => openPreview("schedule_opening", channel)} />
                    </label>
                  ) : null}
                </div>
                <div className="space-y-3">
                  {!draft ? (
                    <div className="rounded-md border border-slate-100 bg-slate-50 p-4 text-sm text-slate-500">
                      Pick an action to generate a preview.
                    </div>
                  ) : (
                    <>
                      {draft.intent || draft.rationale ? (
                        <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
                          {draft.intent ? <div className="font-medium">Intent: {draft.intent.replace(/_/g, " ")}</div> : null}
                          {draft.rationale ? <div className="mt-1">{draft.rationale}</div> : null}
                        </div>
                      ) : null}
                      {draft.guard?.warning ? (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                          {draft.guard.reason}
                        </div>
                      ) : null}
                      {channel === "email" ? (
                        <label className="block">
                          <span className="text-xs font-medium text-slate-600">Subject</span>
                          <Input className="mt-1" value={subject} onChange={(event) => setSubject(event.target.value)} />
                        </label>
                      ) : null}
                      <label className="block">
                        <span className="text-xs font-medium text-slate-600">{channel === "sms" ? `SMS body (${smsBodyHasQualityCap ? `${body.length}/480` : body.length})` : "Body"}</span>
                        <textarea
                          className={`mt-1 w-full rounded-md border border-slate-200 bg-white p-3 text-base text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-200 md:text-sm ${channel === "sms" ? "min-h-[140px] md:min-h-[160px]" : "min-h-[240px] md:min-h-[360px]"}`}
                          value={body}
                          onChange={(event) => setBody(event.target.value)}
                          maxLength={smsBodyHasQualityCap ? 480 : undefined}
                        />
                      </label>
                      {send.isError ? <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">{String(send.error.message)}</div> : null}
                      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button className="w-full sm:w-auto" variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                        {send.isError && canForceSend(send.error) ? (
                          <Button className="w-full sm:w-auto" variant="secondary" onClick={() => sendDraft(true)} disabled={send.isPending || !body.trim()}>
                            Send anyway
                          </Button>
                        ) : null}
                        <Button className="w-full sm:w-auto" onClick={() => sendDraft(false)} disabled={send.isPending || (channel === "email" && !subject.trim()) || !body.trim()}>
                          {send.isPending ? "Sending..." : channel === "sms" ? "Send SMS" : "Send email"}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  const action = data.next_best_action;
  const critical = action.priority === "critical";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm md:min-w-[280px]">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Quote Action</div>
      <div className="mt-1 text-sm font-semibold text-slate-950">{action.action.replace(/_/g, " ")}</div>
      <p className="mt-1 text-xs text-slate-500">{action.reason}</p>
      <Button className="mt-3 w-full" variant={critical ? "default" : "secondary"}>
        {critical ? "Call Now" : "Open Vonigo"}
      </Button>
    </div>
  );
}

function MarkJustificationDialog({
  data,
  label,
  danger,
  reason,
  comment,
  pending,
  errorMessage,
  onReasonChange,
  onCommentChange,
  onCancel,
  onConfirm,
}: {
  data: Quote360;
  label: string;
  danger: boolean;
  reason: string;
  comment: string;
  pending: boolean;
  errorMessage: string | null;
  onReasonChange: (value: string) => void;
  onCommentChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const lastInbound = latestInboundEvent(data.timeline);
  const commentLength = comment.trim().length;
  const commentRequired = requiresMarkComment(reason);
  const valid = isMarkJustificationValid(reason, comment);
  return (
    <div className="mobile-dialog-backdrop">
      <div className="mobile-dialog-panel max-w-xl p-4 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-950">
              Mark quote #{data.quote.quote_id} as {label}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {danger
                ? "This stops all follow-up for this quote. Marc reads every close with its reason in the daily brief."
                : "This snoozes the follow-up. Marc reads every handled mark with its reason in the daily brief."}
            </p>
          </div>
          <Button className="shrink-0" variant="ghost" onClick={onCancel}>Close</Button>
        </div>
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Customer&apos;s last message
          </div>
          {lastInbound ? (
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{lastInbound.displayText}</p>
          ) : (
            <p className="mt-1 text-sm text-slate-500">No inbound reply on file for this quote.</p>
          )}
          {lastInbound ? (
            <p className="mt-1 text-xs text-slate-500">Received {formatDate(lastInbound.event.ts)}</p>
          ) : null}
        </div>
        <label className="mt-4 block">
          <span className="text-xs font-medium text-slate-600">Action or reason</span>
          <select
            className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
          >
            <option value="">Pick an action or reason...</option>
            {MARK_REASON_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="mt-3 block">
          <span className="text-xs font-medium text-slate-600">
            {commentRequired
              ? `Comment (${commentLength}/${MIN_MARK_COMMENT_CHARS} min)`
              : "Comment (optional)"}
          </span>
          <textarea
            className="mt-1 min-h-[80px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            value={comment}
            onChange={(event) => onCommentChange(event.target.value)}
            placeholder={commentRequired
              ? "Explain what happened with this customer."
              : "Add a note only if useful."}
          />
        </label>
        {errorMessage ? (
          <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{errorMessage}</div>
        ) : null}
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button className="w-full sm:w-auto" variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button className="w-full sm:w-auto" disabled={!valid || pending} onClick={onConfirm}>
            {pending ? "Saving..." : `Confirm ${label}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function QuickMarkButton({
  label,
  disabled,
  description,
  danger = false,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  description: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`w-full rounded-md border px-3 py-2 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
        danger
          ? "border-rose-200 bg-white text-rose-900 hover:bg-rose-50"
          : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
      }`}
    >
      <span className="block text-xs font-semibold">{label}</span>
      <span className={`mt-0.5 block text-[11px] leading-snug ${danger ? "text-rose-700" : "text-slate-500"}`}>
        {description}
      </span>
    </button>
  );
}

function HelpTip({ text, compact = false }: { text: string; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="Show help"
        className={`${compact ? "w-7 border-l border-slate-100" : "h-5 w-5"} inline-flex items-center justify-center rounded text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200`}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onClick={() => setOpen((value) => !value)}
      >
        ?
      </button>
      {open ? (
        <span className="absolute right-0 top-7 z-30 w-64 rounded-md border border-slate-200 bg-white p-3 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-slate-600 shadow-lg">
          {text}
        </span>
      ) : null}
    </span>
  );
}

function labelForAction(action: QuoteActionType | null | undefined) {
  if (action === "manual_reply") return "Manual reply";
  if (action === "personalized_reply") return "Draft personalized reply";
  if (action === "wisetack") return "Send Wisetack option";
  if (action === "schedule_opening") return "Offer schedule opening";
  if (action === "before_after_gallery") return "Send before/after gallery";
  return "Send helpful follow-up";
}

function primaryButtonLabel(action: QuoteActionType | null | undefined) {
  if (action === "manual_reply") return "Write reply";
  if (action === "personalized_reply") return "Draft reply";
  if (action === "wisetack") return "Preview Wisetack";
  if (action === "schedule_opening") return "Preview opening";
  if (action === "before_after_gallery") return "Preview gallery";
  return "Preview follow-up";
}

function blockedActionLabel(code?: string) {
  if (code === "accepted_reply_call_now") return "Call now";
  if (code === "conversation_marked_handled") return "Already handled";
  if (code === "already_replied_after_latest_inbound") return "Already replied";
  return "No follow-up allowed";
}

function canForceSend(error: unknown) {
  const payload = (error as Error & { payload?: { code?: string } })?.payload;
  return payload?.code === "recent_outbound"
    || payload?.code === "already_replied_after_latest_inbound"
    || payload?.code === "conversation_marked_handled"
    || String((error as Error)?.message || "").includes("last 24 hours");
}

function reasonForAction(action: QuoteActionType) {
  if (action === "manual_reply") return "Human-composed reply. Not part of the follow-up cadence.";
  if (action === "personalized_reply") return "Reads the latest reply and adapts the tone.";
  if (action === "before_after_gallery") return "Sends the before/after gallery when photos would help.";
  if (action === "wisetack") return "For price hesitation or high-value open quotes.";
  if (action === "schedule_opening") return "Use only when capacity is genuinely low.";
  return "A simple service-first check-in.";
}
